import { Box, Paper, PaperProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React, { forwardRef, PropsWithChildren, ReactNode, useCallback } from 'react';
import FlexBox from 'Components/FlexBox';
import useMeasure from 'react-use/lib/useMeasure';

interface IPaperWithAddItemProps extends PropsWithChildren<PaperProps> {
  infoMessage?: string;
  SearchComponent?: ReactNode;
  'data-testid'?: string;
  maxHeight?: number | string;
  ActionComponent?: ReactNode;
}

const PaperContainer = styled(Paper)(({ theme }) => ({
  paddingBottom: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
}));

const ScrollableContent = styled(Box)({
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
});

/**
 * This adds an add button to the bottom of the paper
 * Can be used with the component prop PaperComponent -> business-app/src/components/Partners/Form/PartnerAutocomplete.tsx
 */
const PaperWithAddItem = forwardRef<HTMLDivElement, IPaperWithAddItemProps>(
  (
    {
      infoMessage,
      children,
      maxHeight,
      SearchComponent,
      ActionComponent,
      'data-testid': dataTestId,
      ...rest
    },
    forwardedRef
  ) => {
    const [measureRef, { width: selectWidth }] = useMeasure();

    const mergedRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (node) {
          measureRef(node);
        }
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef, measureRef]
    );

    return (
      <PaperContainer ref={mergedRef} data-testid={dataTestId} {...rest}>
        {infoMessage && (
          <FlexBox px={2} pt={2} mb={1} gap={1} maxWidth={selectWidth}>
            <Icon name="info" />
            {infoMessage}
          </FlexBox>
        )}

        {SearchComponent}

        <ScrollableContent maxHeight={maxHeight}>{children}</ScrollableContent>

        {ActionComponent}
      </PaperContainer>
    );
  }
);

PaperWithAddItem.displayName = 'PaperWithAddItem';

export default PaperWithAddItem;
